import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { changeRequestsAPI, projectsAPI, usersAPI, exportAPI, downloadBlob } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import ChangeRequestFormFields, { crFormValid, crToForm, emptyCRForm } from '../components/ChangeRequestFormFields';
import { PageHeader, Badge, SearchInput, Modal, EmptyState, ConfirmDialog } from '../components/layout/UI';
import { format } from 'date-fns';
import { apiError, useConfirm, useToast } from '../components/Toast';
import { usePagePerms } from '../PagePermsContext';
import { useMasters } from '../hooks/useMasters';
import { Download, Eye, GitPullRequest, Plus, RotateCcw, SquarePen, Trash2 } from 'lucide-react';
import Pager, { usePaging } from '../components/Pager';
import { useSelection, SelectAll, RowCheck, BulkBar } from '../components/BulkActions';

// ── Icons ─────────────────────────────────────────────────────────────────
const PlusIcon = () => <Plus size={14} />;
const PencilIcon = () => <SquarePen size={13} />;
const TrashIcon = () => <Trash2 size={13} />;
const DlIcon = () => <Download size={14} />;

// ── Constants ─────────────────────────────────────────────────────────────
// All ten values from change_requests_approval_status_check.
/* Fallback only; the live list comes from Masters inside the component,
 * the same category ChangeRequestFormFields reads. */
const APPROVAL_FALLBACK = ['Pending','Under Review','Requirements Shared','BRD Signed Off',
                       'Approved','In Development','Testing','Deployed',
                       'Implemented','Rejected'].map(v=>({value:v,label:v}));

const ENV_STYLE = {
  Production:  'border border-red-500 text-red-600 dark:text-red-400',
  UAT:         'border border-amber-500 text-amber-600 dark:text-amber-400',
  Development: 'border border-blue-500 text-blue-600 dark:text-blue-400',
};

const defaultForm = {
  project_id:'', title:'', description:'', business_justification:'',
  impact_level:'Medium', approval_status:'Pending',
  implementation_date:'', environment:'Development', assigned_to:''
};

export default function ChangeRequestsPage() {
  // Admin → Page Access decides whether this role may create here.
  const { canWrite } = usePagePerms();
  const m = useMasters({ approval: ['cr_status', APPROVAL_FALLBACK] });
  const toast = useToast();
  const confirm = useConfirm();
  const { user: me } = useAuth();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('project_id') || '';

  const navigate = useNavigate();
  const [items,     setItems]     = useState([]);
  const [users,     setUsers]     = useState([]);
  const [projects,  setProjects]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [scope, setScope] = useState('all');   // all | mine | deleted
  // One pager for every list, so they all count and phrase it the same way.
  const paging = usePaging(scope);
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [detailItem,setDetailItem]= useState(null);   // for detail view modal
  const [editItem,  setEditItem]  = useState(null);
  const [form,      setForm]      = useState({ ...emptyCRForm(), project_id: projectFilter });
  const [saving,    setSaving]    = useState(false);
  const [deleteId,  setDeleteId]  = useState(null);



  const restoreRow = async (row) => {
    try { await changeRequestsAPI.restore(row.id); toast.success('Restored'); load(); }
    catch (e) { toast.error(apiError(e, 'Could not restore it.')); }
  };

  const discardRow = async (row) => {
    const ok = await confirm(
      'Remove this from your Deleted tab? You will not be able to see it after '
      + 'this, though an administrator can still restore it.',
      { title: 'Remove from Deleted', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await changeRequestsAPI.discard(row.id); toast.success('Removed from your Deleted tab'); load(); }
    catch (e) { toast.error(apiError(e, 'Could not remove it.')); }
  };
  const load = useCallback(async () => {
    try {
      const [res, pRes, uRes] = await Promise.all([
        changeRequestsAPI.getAll({ project_id: projectFilter, scope: scope === 'deleted' ? 'deleted' : undefined, mine: scope === 'mine' ? 'true' : undefined, limit: paging.limit, offset: paging.offset }),
        projectsAPI.getAll({ limit: 0 }),
        usersAPI.getAll()
      ]);
      setItems(res.data);
      paging.readHeaders(res);
      setProjects(pRes.data);
      setUsers(uRes.data || []);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [projectFilter, scope, paging.offset]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ ...emptyCRForm(), project_id: projectFilter, assigned_to: me?.id || '' });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm(crToForm(item));
    setDetailItem(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      editItem ? await changeRequestsAPI.update(editItem.id, form) : await changeRequestsAPI.create(form);
      toast.success(editItem ? 'Change request updated' : 'Change request submitted');
      setModalOpen(false);
      load();
    } catch(e) { toast.error(apiError(e, 'Failed to save change request.')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await changeRequestsAPI.delete(deleteId);
    toast.success('Change request deleted'); setDeleteId(null); load(); } catch(e) { toast.error(apiError(e, 'Failed to delete.')); }
  };

  const handleInlineStatus = async (item, newStatus) => {
    await changeRequestsAPI.update(item.id, { ...item, approval_status: newStatus });
    toast.success('Change request updated');
    load();
  };

  const filtered = items.filter(i =>
    (!statusFilter || i.approval_status === statusFilter) &&
    (!search || i.title.toLowerCase().includes(search.toLowerCase()) || i.project_name?.toLowerCase().includes(search.toLowerCase()))
  );

  // Ticked rows. Scoped to what is on screen, so a filter cannot hide
  // something that is still selected.
  // Declared AFTER `filtered`, deliberately: a const cannot be read
  // above its own declaration, and doing so crashes the page at render
  // with "Cannot access 'filtered' before initialization".
  const sel = useSelection(filtered);

  // ── Stat counts ───────────────────────────────────────────────────────────
  const counts = {
    total:       items.length,
    pending:     items.filter(i => i.approval_status === 'Pending').length,
    approved:    items.filter(i => i.approval_status === 'Approved').length,
    implemented: items.filter(i => i.approval_status === 'Implemented').length,
  };

  return (
    <div>
      <PageHeader
        title="Change Requests"
        subtitle="Manage change requests and approvals"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={async()=>{ const r=await exportAPI.exportChangeRequests(projectFilter); downloadBlob(r.data,'change-requests.xlsx'); }} className="btn-secondary flex items-center gap-1.5 text-sm"><DlIcon /> Export</button>
            {canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIcon /> New Request</button>)}
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { l:'Total',       v: counts.total,       color:'#2E539F' },
          { l:'Pending',     v: counts.pending,     color:'#f59e0b' },
          { l:'Approved',    v: counts.approved,    color:'#10b981' },
          { l:'Implemented', v: counts.implemented, color:'#3b82f6' },
        ].map(s => (
          <div key={s.l} className="stat-card">
            <span className="stat-label">{s.l}</span>
            <p className="text-3xl font-bold" style={{ color:'var(--text)' }}>{s.v}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex-1 min-w-48 max-w-xs">
          <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search change requests..." />
        </div>
        {/* All / Mine / Deleted. Bugs and change requests were the last two
            entities where deleting destroyed the record. */}
        <div className="flex rounded-lg border overflow-hidden text-sm self-start"
          style={{ borderColor:'var(--border)' }}>
          {[['all','All'],['mine','Mine'],['deleted','Deleted']].map(([v,l]) => (
            <button key={v} onClick={() => setScope(v)}
              className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${scope===v ? 'bg-brand-500 text-white' : ''}`}
              style={scope!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
              {v === 'deleted' && <Trash2 size={13} />}
              {l}
            </button>
          ))}
        </div>
        <select className="select w-44 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {m.approval.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={GitPullRequest}
            title="No change requests"
            description="Submit your first change request"
            action={canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIcon /> New Request</button>)}
          />
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                <th className="px-4 py-3 w-10">
                  <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                    onToggle={sel.toggleAll} />
                </th>
                {['Title','Project','Impact','Status','Environment','Requested By','Target Date',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors cursor-pointer" style={{ borderColor:'var(--border)' }}
                  onClick={() => navigate(`/change-requests/${item.id}`)}>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <RowCheck checked={sel.isSelected(item.id)}
                        onToggle={() => sel.toggle(item.id)} label={item.title} />
                    </td>
                  {/* Title */}
                  <td className="px-4 py-3">
                    <p className="font-medium" style={{ color:'var(--text)' }}>{item.title}</p>
                    {item.business_justification && (
                      <p className="text-xs mt-0.5 line-clamp-1" style={{ color:'var(--text-secondary)' }}>{item.business_justification}</p>
                    )}
                  </td>

                  {/* Project */}
                  <td className="px-4 py-3 text-xs hidden md:table-cell" style={{ color:'var(--text-secondary)' }}>{item.project_name}</td>

                  {/* Impact */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <Badge value={item.impact_level} type="priority" />
                  </td>

                  {/* Status — inline select */}
                  <td className="px-4 py-3">
                    <select
                      value={item.approval_status}
                      onChange={e => handleInlineStatus(item, e.target.value)}
                      className="text-xs rounded-lg px-2 py-1 border font-medium cursor-pointer outline-none"
                      style={{ background:'var(--surface)', borderColor:'var(--border)', color:'var(--text)' }}>
                      {m.approval.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>

                  {/* Environment */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {item.environment && (
                      <span className={`badge ${ENV_STYLE[item.environment] || ''}`}>{item.environment}</span>
                    )}
                  </td>

                  {/* Requested by */}
                  <td className="px-4 py-3 hidden xl:table-cell text-xs" style={{ color:'var(--text-secondary)' }}>
                    {item.requested_by_name || '—'}
                  </td>

                  {/* Target date */}
                  <td className="px-4 py-3 hidden xl:table-cell text-xs" style={{ color:'var(--text-secondary)' }}>
                    {item.implementation_date ? format(new Date(item.implementation_date), 'MMM d, yyyy') : '—'}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => setDetailItem(item)}
                        className="p-1.5 rounded hover:bg-[var(--surface-alt)] transition-colors"
                        style={{ color:'var(--text-secondary)' }} title="View details">
                        <Eye size={13}/>
                      </button>
                      {scope === 'deleted' ? (
                        <>
                          {/* In the bin: put it back, or take it out of your own Deleted tab. */}
                          <button onClick={e=>{e.stopPropagation();restoreRow(item);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-[var(--surface-alt)]"
                            style={{ borderColor:'var(--border)', color:'#2E539F' }}>
                            <RotateCcw size={12} /> Restore
                          </button>
                          <button onClick={e=>{e.stopPropagation();discardRow(item);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-red-50 dark:hover:bg-red-900/20"
                            style={{ borderColor:'var(--border)', color:'#ef4444' }}>
                            <Trash2 size={12} /> Delete
                          </button>
                        </>
                      ) : (
                        <>
                      <button onClick={() => openEdit(item)}
                        className="p-1.5 rounded hover:bg-[var(--surface-alt)] transition-colors"
                        style={{ color:'var(--text-secondary)' }}>
                        <PencilIcon />
                      </button>
                      <button onClick={() => setDeleteId(item.id)}
                        className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <TrashIcon />
                      </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {detailItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDetailItem(null)} />
          <div className="relative rounded-xl shadow-2xl w-full max-w-lg z-10" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor:'var(--border)' }}>
              <h2 className="font-semibold" style={{ color:'var(--text)' }}>{detailItem.title}</h2>
              <button onClick={() => setDetailItem(null)} className="p-1.5 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}>✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge value={detailItem.approval_status} type="status" />
                <Badge value={detailItem.impact_level} type="priority" />
                {detailItem.environment && <span className={`badge ${ENV_STYLE[detailItem.environment]||''}`}>{detailItem.environment}</span>}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="label">Project</p><p style={{ color:'var(--text)' }}>{detailItem.project_name}</p></div>
                <div><p className="label">Requested By</p><p style={{ color:'var(--text)' }}>{detailItem.requested_by_name || '—'}</p></div>
                <div><p className="label">Created</p><p style={{ color:'var(--text)' }}>{format(new Date(detailItem.created_at),'MMM d, yyyy')}</p></div>
                <div><p className="label">Target Date</p><p style={{ color:'var(--text)' }}>{detailItem.implementation_date ? format(new Date(detailItem.implementation_date),'MMM d, yyyy') : '—'}</p></div>
              </div>
              {detailItem.description && (
                <div><p className="label">Description</p><p className="text-sm" style={{ color:'var(--text)' }}>{detailItem.description}</p></div>
              )}
              {detailItem.business_justification && (
                <div className="p-3 rounded-lg" style={{ background:'var(--surface-alt)' }}>
                  <p className="label mb-1">Business Justification</p>
                  <p className="text-sm" style={{ color:'var(--text)' }}>{detailItem.business_justification}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t" style={{ borderColor:'var(--border)' }}>
              <button onClick={() => setDetailItem(null)} className="btn-secondary">Close</button>
              <button onClick={() => openEdit(detailItem)} className="btn-primary flex items-center gap-1.5"><PencilIcon /> Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editItem ? 'Edit Change Request' : 'New Change Request'} size="lg">
        <div className="space-y-4">
          {/* Shared with DevHubPage — add fields in ChangeRequestFormFields.jsx. */}
          <ChangeRequestFormFields form={form} setForm={setForm}
            projects={projects} users={users} me={me} showStatus />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving || !crFormValid(form)} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              {editItem ? 'Update' : 'Submit'} Request
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Delete Change Request" message="Delete this change request permanently?" />

    <Pager {...paging} onChange={paging.setOffset} noun="change requests" />
    <BulkBar bulk={changeRequestsAPI.bulk} view={scope === 'deleted' ? 'deleted' : 'live'}
      selected={sel.selected} label="change request" labelPlural="change requests"
      onClear={sel.clear} onDone={load} />
    </div>
  );
}
